/**
 * Component: Size Chart
 * Adds a metric/imperial unit toggle to the size chart table rendered by
 * `snippets/size-chart-content.liquid`. Converts table cell values and header
 * unit labels between metric (cm/kg) and imperial (in/lbs) on the fly.
 *
 * Shares the `sizeGuideImperial` sessionStorage flag with the size quiz and
 * stays in sync with it via the `size-guide:unit-change` document event, so a
 * buyer's unit choice is consistent across the whole size guide dock-modal.
 *
 * Wrapped in a load guard so that re-injecting the script tag (e.g. section
 * reload in the theme editor) does not redeclare `SizeChart` or re-run init.
 */
if (!window.SizeChart) {

  /* Convert a metric cell value (cm/kg, single values and ranges) to imperial. */
  const convertCellToImperial = (text) =>
    text
      .replace(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*([-\u2013])\s*(\d+(?:\.\d+)?)\s*cm/gi, (_m, lo, dash, hi) => {
        return `${(parseFloat(lo) / 2.54).toFixed(1)}${dash}${(parseFloat(hi) / 2.54).toFixed(1)}"`;
      })
      .replace(/(\d+(?:\.\d+)?)\s*(?:kg)?\s*([-\u2013])\s*(\d+(?:\.\d+)?)\s*kg/gi, (_m, lo, dash, hi) => {
        return `${(parseFloat(lo) * 2.205).toFixed(1)}${dash}${(parseFloat(hi) * 2.205).toFixed(1)}lbs`;
      })
      .replace(/(\d+(?:\.\d+)?)\s*cm/gi, (_m, val) => `${(parseFloat(val) / 2.54).toFixed(1)}"`)
      .replace(/(\d+(?:\.\d+)?)\s*kg/gi, (_m, val) => `${(parseFloat(val) * 2.205).toFixed(1)}lbs`);

  /* Convert a metric header unit label to imperial. */
  const convertHeaderToImperial = (text) =>
    text
      .replace(/\(cm\)/gi, '(in)')
      .replace(/\bcm\b/gi, 'in')
      .replace(/\(kg\)/gi, '(lbs)')
      .replace(/\bkg\b/gi, 'lbs');

  class SizeChart {
    /** @type {Set<SizeChart>} */
    static #instances = new Set();
    /** @type {boolean} */
    static #listening = false;

    /** @type {HTMLElement} */
    #container;
    /** @type {HTMLTableElement} */
    #table;
    /** @type {NodeListOf<HTMLButtonElement>} */
    #buttons;
    /** @type {boolean} */
    #imperial = false;

    constructor(container) {
      this.#container = container;
      this.#table = container.querySelector('.js-size-chart-table');
      this.#buttons = container.querySelectorAll('.js-size-chart-unit-btn');

      if (!this.#table || !this.#buttons.length) return;

      SizeChart.#prune();
      SizeChart.#instances.add(this);
      SizeChart.#ensureDocumentListener();

      this.#bindEvents();
      this.#restoreUnitPreference();
    }

    static #prune() {
      for (const instance of SizeChart.#instances) {
        if (!instance.#container.isConnected) {
          SizeChart.#instances.delete(instance);
        }
      }
    }

    static #ensureDocumentListener() {
      if (SizeChart.#listening) return;

      SizeChart.#listening = true;

      /* One document listener for all charts. Combined listing colour swaps
         construct a new SizeChart; without this, each swap leaked a listener. */
      document.addEventListener('size-guide:unit-change', (e) => {
        SizeChart.#onDocumentUnitChange(e);
      });
    }

    static #onDocumentUnitChange(e) {
      if (!e.detail) return;

      SizeChart.#prune();

      const isImperial = Boolean(e.detail.imperial);

      for (const instance of SizeChart.#instances) {
        if (e.detail.source === instance) continue;
        if (isImperial === instance.#imperial) continue;

        instance.#setUnit(isImperial, false);
      }
    }

    #bindEvents() {
      for (const btn of this.#buttons) {
        btn.addEventListener('click', () => {
          this.#setUnit(btn.dataset.unit === 'imperial', true);
        });
      }
    }

    #restoreUnitPreference() {
      this.#setUnit(sessionStorage.getItem('sizeGuideImperial') === '1', false);
    }

    #setUnit(isImperial, emit) {
      this.#imperial = isImperial;
      sessionStorage.setItem('sizeGuideImperial', isImperial ? '1' : '0');
      this.#convertTable(isImperial);
      this.#syncButtons(isImperial);

      if (emit) {
        document.dispatchEvent(
          new CustomEvent('size-guide:unit-change', {
            detail: { imperial: isImperial, source: this },
          }),
        );
      }
    }

    #syncButtons(isImperial) {
      const activeUnit = isImperial ? 'imperial' : 'metric';

      for (const btn of this.#buttons) {
        const active = btn.dataset.unit === activeUnit;
        btn.classList.toggle('size-chart__unit-btn--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    }

    #convertTable(toImperial) {
      for (const cell of this.#table.querySelectorAll('td')) {
        if (!cell.hasAttribute('data-original')) {
          cell.setAttribute('data-original', cell.textContent.trim());
        }

        const original = cell.getAttribute('data-original');
        cell.textContent = toImperial ? convertCellToImperial(original) : original;
      }

      const headerCells = this.#table.querySelectorAll('.size-chart__th-title, .size-chart__th-subheading');
      const headers = headerCells.length ? headerCells : this.#table.querySelectorAll('th');

      for (const header of headers) {
        if (!header.hasAttribute('data-original')) {
          header.setAttribute('data-original', header.textContent.trim());
        }

        const original = header.getAttribute('data-original');
        header.textContent = toImperial ? convertHeaderToImperial(original) : original;
      }
    }
  }

  window.SizeChart = SizeChart;
}

/* ==========================================================================
   Auto-init
   Runs on every script load (the class declaration above is guarded so the
   init below is the only thing that re-runs when the script tag is re-injected,
   e.g. on section reload in the theme editor).
   ========================================================================== */
const initSizeChartElement = (el) => {
  if (el.dataset.sizeChartInit === 'true') return;

  el.dataset.sizeChartInit = 'true';
  new window.SizeChart(el);
};

window.initSizeChartElement = initSizeChartElement;

const initSizeCharts = () => {
  for (const el of document.querySelectorAll('.size-chart-content')) {
    initSizeChartElement(el);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSizeCharts);
} else {
  initSizeCharts();
}
